import React, { useState } from "react";
import {
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaCrown,
  FaUsers,
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
} from "react-icons/fa";
import { createEmailUser } from "../services/emailAuthService";
import { checkDuplicateEmail } from "../utils/emailUserUtils";
import { getDatabase, ref, set, get } from "firebase/database";
import { app } from "../firebase/config";
import { hashPassword, generateSecurePassword } from "../utils/passwordUtils";

// Custom function to create email user with custom expiry date
const createEmailUserWithCustomExpiry = async (
  email,
  password,
  displayName,
  adminId,
  membership = null,
  customExpiryTimestamp = null
) => {
  try {
    console.log("🔄 Creating email user with custom expiry:", email);

    if (!email || !password || !adminId) {
      return {
        success: false,
        error: "Email, password, and admin ID are required",
        userId: null,
      };
    }

    // Use email as display name if not provided
    const finalDisplayName = displayName || email.split("@")[0];

    // Check for duplicate email
    const isDuplicate = await checkEmailExists(email);
    if (isDuplicate) {
      return {
        success: false,
        error: "Email already exists",
        userId: null,
      };
    }

    // Generate unique user ID
    const userId = generateEmailUserId();

    // Hash password
    const passwordHash = await hashPassword(password);

    // Use custom expiry timestamp if provided, otherwise calculate default
    let membershipExpiry = customExpiryTimestamp;
    if (membership && !customExpiryTimestamp) {
      const expiryDate = new Date();
      switch (membership) {
        case 1: // 7 days
          expiryDate.setDate(expiryDate.getDate() + 7);
          break;
        case 2: // 30 days
          expiryDate.setDate(expiryDate.getDate() + 30);
          break;
        case 3: // 180 days
          expiryDate.setDate(expiryDate.getDate() + 180);
          break;
        case 4: // 360 days
          expiryDate.setDate(expiryDate.getDate() + 360);
          break;
        default:
          expiryDate.setDate(expiryDate.getDate() + 30);
      }

      // Round up to next hour
      expiryDate.setMinutes(0);
      expiryDate.setSeconds(0);
      expiryDate.setMilliseconds(0);
      expiryDate.setHours(expiryDate.getHours() + 1);

      membershipExpiry = expiryDate.getTime();
    }

    // Prepare user data
    const userData = {
      userID: userId,
      createdAt: Date.now(),
      dailyPostCount: {},
      profile: {
        displayName: finalDisplayName,
        email: email,
        photoURL: null,
        authProvider: "email",
      },
      emailAuth: {
        email: email,
        passwordHash: passwordHash,
        isActive: true,
        lastLogin: null,
        createdBy: adminId,
        createdAt: Date.now(),
      },
    };

    // Add membership if provided
    if (membership && membershipExpiry) {
      userData.membership = membership;
      userData.membershipExpiry = membershipExpiry;
    }

    // Save user to database
    const database = getDatabase(app);
    const userRef = ref(database, `users/${userId}`);
    await set(userRef, userData);

    // OPTIMIZED: Also save to user_profiles
    try {
      const userProfileData = {
        id: userId,
        createdAt: userData.createdAt,
        profile: userData.profile,
        emailAuth: userData.emailAuth,
        membership: userData.membership || null,
        membershipExpiry: userData.membershipExpiry || null,
        stats: { postCount: 0, createdPostCount: 0 }
      };
      await set(ref(database, `user_profiles/${userId}`), userProfileData);
    } catch (e) {
      console.error("Error creating user_profile:", e);
    }

    console.log(
      "✅ Email user created successfully with custom expiry:",
      userId
    );

    return {
      success: true,
      error: null,
      userId: userId,
      user: userData,
    };
  } catch (error) {
    console.error("❌ Error creating email user with custom expiry:", error);
    return {
      success: false,
      error: "Failed to create user. Please try again.",
      userId: null,
    };
  }
};

// Helper functions (copied from emailAuthService.js)
const checkEmailExists = async (email) => {
  try {
    const database = getDatabase(app);
    // OPTIMIZED: Use user_profiles instead of users
    const usersRef = ref(database, "user_profiles");
    const snapshot = await get(usersRef);

    if (!snapshot.exists()) {
      return false;
    }

    const users = snapshot.val();

    // Check in both Google users and email users
    for (const userData of Object.values(users)) {
      // Check Google users
      if (userData.profile?.email === email) {
        return true;
      }
      // Check email auth users
      if (userData.emailAuth?.email === email) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error("❌ Error checking email existence:", error);
    return false;
  }
};

const generateEmailUserId = () => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `email_${timestamp}_${randomStr}`;
};

const AdminBulkEmailUserCreation = ({ onUsersCreated, onCancel, adminId }) => {
  // Get default end date for initial membership selection
  const getInitialEndDate = () => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 7); // Default 7 days for membership "1"
    return endDate.toISOString().split("T")[0];
  };

  const [formData, setFormData] = useState({
    emailList: "",
    password: "123456",
    membership: "1",
    membershipEndDate: getInitialEndDate(),
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [creationResults, setCreationResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // Membership options
  const membershipOptions = [
    { value: "", label: "Miễn phí (Không có gói thành viên)" },
    { value: 1, label: "7 ngày" },
    { value: 2, label: "30 ngày" },
    { value: 3, label: "180 ngày" },
    { value: 4, label: "360 ngày" },
  ];

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Clear general error
    if (errors.general) {
      setErrors((prev) => ({
        ...prev,
        general: "",
      }));
    }
  };

  // Get default end date based on membership selection
  const getDefaultEndDate = (membershipType) => {
    if (!membershipType) return "";

    const today = new Date();
    let endDate = new Date(today);

    switch (parseInt(membershipType)) {
      case 1: // 7 days
        endDate.setDate(today.getDate() + 7);
        break;
      case 2: // 30 days
        endDate.setDate(today.getDate() + 30);
        break;
      case 3: // 180 days
        endDate.setDate(today.getDate() + 180);
        break;
      case 4: // 360 days
        endDate.setDate(today.getDate() + 360);
        break;
      default:
        return "";
    }

    // Format as YYYY-MM-DD for date input
    return endDate.toISOString().split("T")[0];
  };

  // Handle membership change and auto-set end date
  const handleMembershipChange = (e) => {
    const membershipValue = e.target.value;
    const defaultEndDate = getDefaultEndDate(membershipValue);

    setFormData((prev) => ({
      ...prev,
      membership: membershipValue,
      membershipEndDate: defaultEndDate,
    }));

    // Clear errors
    if (errors.membership) {
      setErrors((prev) => ({
        ...prev,
        membership: "",
      }));
    }
    if (errors.membershipEndDate) {
      setErrors((prev) => ({
        ...prev,
        membershipEndDate: "",
      }));
    }
  };

  // Parse email list
  const parseEmailList = (emailString) => {
    if (!emailString.trim()) return [];

    // Split by various delimiters and clean up
    const emails = emailString
      .split(/[\s,;\n]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    return emails;
  };

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Validate email list
    if (!formData.emailList.trim()) {
      newErrors.emailList = "Danh sách email là bắt buộc";
    } else {
      const emails = parseEmailList(formData.emailList);
      if (emails.length === 0) {
        newErrors.emailList = "Vui lòng nhập ít nhất một email";
      } else {
        const invalidEmails = emails.filter((email) => !isValidEmail(email));
        if (invalidEmails.length > 0) {
          newErrors.emailList = `Email không hợp lệ: ${invalidEmails.join(
            ", "
          )}`;
        }
      }
    }

    // Validate password (minimum 6 characters)
    if (!formData.password) {
      newErrors.password = "Mật khẩu là bắt buộc";
    } else if (formData.password.length < 6) {
      newErrors.password = "Mật khẩu phải có ít nhất 6 ký tự";
    }

    // Validate membership end date if membership is selected
    if (formData.membership && formData.membership !== "") {
      if (!formData.membershipEndDate) {
        newErrors.membershipEndDate =
          "Ngày kết thúc membership là bắt buộc khi chọn gói thành viên";
      } else {
        const endDate = new Date(formData.membershipEndDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

        if (endDate <= today) {
          newErrors.membershipEndDate = "Ngày kết thúc phải sau ngày hôm nay";
        }
      }
    }

    // Validate admin ID
    if (!adminId) {
      newErrors.general = "ID quản trị viên là bắt buộc";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setErrors({});
    setCreationResults([]);
    setShowResults(false);

    // Validate form
    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    const emails = parseEmailList(formData.emailList);
    setIsLoading(true);

    const results = [];
    let successCount = 0;

    try {
      for (let i = 0; i < emails.length; i++) {
        const email = emails[i];

        try {
          // Check for duplicate email first
          const duplicateCheck = await checkDuplicateEmail(email);
          if (duplicateCheck.isDuplicate) {
            results.push({
              email,
              success: false,
              error: duplicateCheck.error,
              userId: null,
            });
            continue;
          }

          // Calculate custom expiry date if provided
          let customExpiryTimestamp = null;
          if (formData.membership && formData.membershipEndDate) {
            const endDate = new Date(formData.membershipEndDate);
            // Set to end of day (23:59:59)
            endDate.setHours(23, 59, 59, 999);
            customExpiryTimestamp = endDate.getTime();
          }

          // Create user
          const result = await createEmailUserWithCustomExpiry(
            email,
            formData.password,
            null, // No display name
            adminId,
            formData.membership ? parseInt(formData.membership) : null,
            customExpiryTimestamp
          );

          if (result.success) {
            results.push({
              email,
              success: true,
              error: null,
              userId: result.userId,
            });
            successCount++;
          } else {
            results.push({
              email,
              success: false,
              error: result.error,
              userId: null,
            });
          }
        } catch (error) {
          console.error(`Error creating user for ${email}:`, error);
          results.push({
            email,
            success: false,
            error: "Đã xảy ra lỗi không mong muốn",
            userId: null,
          });
        }

        // Add small delay to avoid overwhelming the server
        if (i < emails.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      setCreationResults(results);
      setShowResults(true);

      // Notify parent component if there were successful creations
      if (successCount > 0 && onUsersCreated) {
        onUsersCreated(results.filter((r) => r.success));
      }
    } catch (error) {
      console.error("Error in bulk creation:", error);
      setErrors({
        general: "Đã xảy ra lỗi không mong muốn. Vui lòng thử lại.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get email count
  const getEmailCount = () => {
    if (!formData.emailList.trim()) return 0;
    return parseEmailList(formData.emailList).length;
  };

  // Handle close
  const handleClose = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FaUsers className="mr-2" />
            Tạo Đồng Loạt Người Dùng Email
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Tạo nhiều tài khoản người dùng cùng lúc với danh sách email
          </p>
        </div>

        {!showResults ? (
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {errors.general}
              </div>
            )}

            {/* Email List Input */}
            <div>
              <label
                htmlFor="emailList"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Danh Sách Email *
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-3 text-gray-400" />
                <textarea
                  id="emailList"
                  name="emailList"
                  value={formData.emailList}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.emailList ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="phuocl1@gmail.com phuocl2@gmail.com phuocl3@gmail.com&#10;hoặc&#10;phuocl1@gmail.com&#10;phuocl2@gmail.com&#10;phuocl3@gmail.com"
                  rows={6}
                  disabled={isLoading}
                  required
                />
              </div>
              <div className="mt-1 flex justify-between items-center">
                {errors.emailList ? (
                  <p className="text-sm text-red-600">{errors.emailList}</p>
                ) : (
                  <p className="text-xs text-gray-500">
                    Nhập danh sách email, cách nhau bằng dấu cách, dấu phẩy hoặc
                    xuống dòng
                  </p>
                )}
                <span className="text-xs text-blue-600 font-medium">
                  {getEmailCount()} email
                </span>
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Mật Khẩu Chung *
              </label>
              <div className="relative">
                <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.password ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Nhập mật khẩu chung cho tất cả tài khoản"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              {/* Password Length Indicator */}
              {formData.password && (
                <div className="mt-2">
                  <p className="text-xs text-gray-600">
                    Độ dài mật khẩu: {formData.password.length} ký tự
                    {formData.password.length < 6 && (
                      <span className="text-red-500 ml-1">
                        (tối thiểu 6 ký tự)
                      </span>
                    )}
                  </p>
                </div>
              )}

              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Membership Selection */}
            <div>
              <label
                htmlFor="membership"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Cấp Độ Thành Viên Chung
              </label>
              <div className="relative">
                <FaCrown className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  id="membership"
                  name="membership"
                  value={formData.membership}
                  onChange={handleMembershipChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  {membershipOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Tất cả tài khoản sẽ được tạo với cùng cấp độ thành viên này
              </p>
            </div>

            {/* Membership End Date */}
            {formData.membership && formData.membership !== "" && (
              <div>
                <label
                  htmlFor="membershipEndDate"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Ngày Kết Thúc Membership *
                </label>
                <div className="relative">
                  <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="date"
                    id="membershipEndDate"
                    name="membershipEndDate"
                    value={formData.membershipEndDate}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.membershipEndDate
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                    disabled={isLoading}
                    min={new Date().toISOString().split("T")[0]} // Minimum date is today
                    required
                  />
                </div>
                {errors.membershipEndDate ? (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.membershipEndDate}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    Membership sẽ hết hạn vào cuối ngày đã chọn (23:59:59)
                  </p>
                )}
                {formData.membershipEndDate && (
                  <div className="mt-1">
                    <p className="text-xs text-blue-600">
                      Ngày hết hạn:{" "}
                      {new Date(formData.membershipEndDate).toLocaleDateString(
                        "vi-VN",
                        {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}{" "}
                      lúc 23:59:59
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Form Actions */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                disabled={isLoading}
              >
                Hủy
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center justify-center"
                disabled={isLoading || getEmailCount() === 0}
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Đang tạo...
                  </>
                ) : (
                  `Tạo ${getEmailCount()} Tài Khoản`
                )}
              </button>
            </div>
          </form>
        ) : (
          /* Results Display */
          <div className="px-6 py-4">
            <div className="mb-4">
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                Kết Quả Tạo Tài Khoản
              </h4>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <FaCheckCircle className="text-green-500 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Thành công
                      </p>
                      <p className="text-lg font-bold text-green-900">
                        {creationResults.filter((r) => r.success).length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <FaTimesCircle className="text-red-500 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        Thất bại
                      </p>
                      <p className="text-lg font-bold text-red-900">
                        {creationResults.filter((r) => !r.success).length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Results List */}
            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trạng thái
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Chi tiết
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {creationResults.map((result, index) => (
                    <tr
                      key={index}
                      className={result.success ? "bg-green-50" : "bg-red-50"}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                        {result.email}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {result.success ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <FaCheckCircle className="mr-1" />
                            Thành công
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <FaTimesCircle className="mr-1" />
                            Thất bại
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {result.success ? (
                          <span className="text-green-600">
                            ID: {result.userId}
                          </span>
                        ) : (
                          <span className="text-red-600" title={result.error}>
                            {result.error}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Results Actions */}
            <div className="flex justify-end pt-4">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Đóng
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBulkEmailUserCreation;
